/**
 * Component   : UserManagement
 * Description : Adding / Updating a User
 */

import {
    Box,
    Button,
    Grid,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
    callAPI,
    customValidation,
    EPhone,
    formatPhoneNumber,
    Method,
    updateMainHeader,
    validations,
} from "../../commonFunctions/CommonFunctions";
import WSPhone from "../../Components/Phone/WSPhone";
import WSSelectField from "../../Components/Select/WSSelect";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import clientConfig from "../../Config/clientConfig.json";
import backgroundImage1 from "../../images/bg2.svg";
import doctorImage from "../../images/melp-doctor-login-logo.svg";
import logo from "../../images/melplogo1.svg";

export const UserRegistration: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const { register, handleSubmit, control, setValue, watch, reset } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const id = params?.id;
  const [loading, setLoading] = useState(false);

  useEffect(
    () => () => {
      reset();
    },
    []
  );

  useEffect(() => {
    updateMainHeader("User");
  }, []);

  useEffect(() => {
    if (id) getUserById(id);
  }, [id]);

  const getUserById = async (id: string) => {
    try {
      const response: any = await callAPI("Auth", Method.GET, {}, id);
      if (response) {
        for (const field in response) {
          if (field === "phoneNumber") {
            setValue(
              field,
              formatPhoneNumber(response[field], EPhone.AddHyphen)
            );
          } else {
            setValue(field, response[field]);
          }
        }
      }
    } catch (error) {
      showMessage("Error fetching user: " + error, "error");
    }
  };

  const handleCancelClick = () => {
    navigate("/login");
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const request = {
        name: data?.name,
        email: data?.email,
        userCode: data?.userCode || "",
        userType: data?.userType,
        password: data?.password,
        phoneNumber: formatPhoneNumber(data?.phoneNumber, EPhone.RemoveHyphen),
        deviceToken: "",
      };
      if (id) delete request.password;

      const response: any = id
        ? await callAPI("Auth", Method.PUT, request, id)
        : await callAPI("/Auth/register", Method.POST, request);

      if (response) {
        showMessage(
          response?.message,
          response.isSuccess ? "success" : "error"
        );
        if (response.isSuccess) navigate("/login");
      }
    } catch (e: any) {
      showMessage(e.error || "Error saving user", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid item container>

      <Grid
        container
        sx={{
          position: 'relative',
        //  backgroundImage: `url(${backImg})`,
          backgroundSize: 'cover',
          backgroundColor: '#022219',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
        }}
      >  <Box
          component="img"
          src={logo}
          alt="MELP Logo"
          sx={{
            position: 'absolute',
            top: 24,
            left: 54,
            width: 100,
            zIndex: 10,
          }}
        />
        <Grid container sx={{
          position: 'relative',
          minHeight: '80vh',
          backgroundImage: `url(${backgroundImage1})`,
          border: '1px solid #022219',
          borderRadius: 5,
          backgroundSize: 'cover',
          backgroundColor: '#022219',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          width: '80%',
          padding: 7,
        }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                maxWidth: "600px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <img src={doctorImage} height={100} alt="doctor" />
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#098963" }}
              >
                {"Register"}
              </Typography>
              <Typography variant="body2" color="#098963" sx={{ mb: 2 }}>
                {id
                  ? "Modify user details below"
                  : "Fill in the details to create a new user"}
              </Typography>

              <Grid container spacing={3} sx={{ textAlign: "left" }}>
                <Grid item xs={12} sm={6}>
                  <WSTextField
                    control={control}
                    id="name"
                    {...register("name")}
                    name="name"
                    rules={{ required: true }}
                    label="Name *"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <WSTextField
                    control={control}
                    id="email"
                    {...register("email")}
                    name="email"
                    rules={customValidation(true, validations.email)}
                    label="Email *"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <WSTextField
                    control={control}
                    id="userCode"
                    rules={{ required: !id }}
                    minLength={6}
                    maxLength={6}
                    type={TextType.AllowOnlyNumber}
                    name="userCode"
                    label="PIN"
                    disabled={!!id}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <WSSelectField
                    rules={{ required: true }}
                    control={control}
                    id="userType"
                    {...register("userType")}
                    name="userType"
                    label="User Type *"
                    options={clientConfig.userTypeConfig.staticOptionData}
                  />
                </Grid>

                {!id && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <WSTextField
                        control={control}
                        id="password"
                        {...register("password", {
                          required: true,
                          validate: (value: any) =>
                            value === watch("confirmPassword") ||
                            "Passwords do not match",
                        })}
                        name="password"
                        label="Password *"
                        type={TextType.Password}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <WSTextField
                        control={control}
                        id="confirmPassword"
                        {...register("confirmPassword", {
                          required: true,
                          validate: (value: any) =>
                            value === watch("password") ||
                            "Passwords do not match",
                        })}
                        label="Confirm Password *"
                        type={TextType.Password}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={12}>
                  <WSPhone
                    label="Phone"
                    rules={{ required: true }}
                    control={control}
                    id="phoneNumber"
                    {...register("phoneNumber")}
                    name="phoneNumber"
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    sx={{
                      mr: 2,
                      borderColor: "#098963",
                      color: "#098963",
                      borderRadius: 2,
                      px: 4,
                    }}
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "#098963",
                      borderRadius: 2,
                      px: 4,
                      "&:hover": {
                        backgroundColor: "#066e4f",
                      },
                    }}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Grid>
      </Grid>
    </Grid>
  );
});
